import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import api from '../api';
import { LoanFormData, RulesResult } from '../types';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'
];

const PRODUCT_TYPES = ['Ground up (Sale or Rent)', 'Rental Hold', 'Recap'];

const PROPERTY_TYPES = [
  'Single Family Residence', 'Du/Tri/Quad Plex', 'Multifamily', 'Mixed Use',
  'Condos (1-4 Units)', 'Condos (5+ Units)', 'Townhomes',
  'Land - Entitled', 'Land - Unentitled', 'Commercial',
  'Manufactured Housing/Modular', 'Rental Hold - STVR'
];

const TABS = [
  { id: 'loan',     label: 'Loan Details' },
  { id: 'property', label: 'Property'     },
  { id: 'borrower', label: 'Borrower'     },
  { id: 'fees',     label: 'Fees'         }
];

interface Props {
  defaultValues?: Partial<LoanFormData>;
  onRulesResult: (result: RulesResult) => void;
  onSubmit: (data: LoanFormData) => Promise<void>;
  submitting?: boolean;
  readOnly?: boolean;
  rulesResult?: RulesResult | null;
}

function FF({
  label, error, hint, colSpan, children
}: {
  label: string;
  error?: string;
  hint?: string;
  colSpan?: number;
  children: React.ReactNode;
}) {
  return (
    <div className={colSpan === 2 ? 'col-span-2' : ''}>
      <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wider">
        {label}
      </label>
      {children}
      {hint && !error && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
      {error && <p className="mt-0.5 text-xs text-[#EF4444]">{error}</p>}
    </div>
  );
}

const base = 'w-full bg-[#0F1117] border rounded px-3 py-2 text-white text-sm focus:outline-none transition-colors placeholder-gray-600';
const ic = (breached?: boolean) =>
  `${base} ${breached ? 'border-red-500 focus:border-red-500' : 'border-gray-700 focus:border-[#2563EB]'}`;
const sc = `${ic()} appearance-none cursor-pointer`;

// ── Application name generator ────────────────────────────────────────────────
function ptAbbr(pt?: string) {
  if (!pt) return 'XX';
  if (pt.startsWith('Ground')) return 'GU';
  if (pt.startsWith('Rental')) return 'RH';
  if (pt.startsWith('Recap'))  return 'RC';
  return pt.slice(0, 2).toUpperCase();
}
function pgAbbr(pg?: string) {
  if (!pg) return 'XX';
  return pg === 'Purchase' ? 'PUR' : pg === 'Refinance' ? 'REF' : pg.slice(0, 3).toUpperCase();
}
function tierAbbr(tier?: string) {
  if (!tier) return 'T?';
  const m = tier.match(/\d/);
  return m ? `T${m[0]}` : tier.slice(0, 2).toUpperCase();
}
function dateCode() {
  const d = new Date();
  return String(d.getFullYear()).slice(2) + String(d.getMonth() + 1).padStart(2, '0');
}
function randSuffix() {
  return Math.random().toString(36).toUpperCase().slice(2, 6);
}
function buildAppName(productType?: string, loanProgram?: string, state?: string, tier?: string) {
  return `${ptAbbr(productType)}-${pgAbbr(loanProgram)}-${(state || 'XX').toUpperCase()}-${tierAbbr(tier)}-${dateCode()}-${randSuffix()}`;
}

export default function LoanForm({
  defaultValues, onRulesResult, onSubmit, submitting, readOnly, rulesResult
}: Props) {
  const [activeTab, setActiveTab] = useState('loan');
  const [appName, setAppName] = useState<string>('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const { register, handleSubmit, watch, formState: { errors } } = useForm<LoanFormData>({
    defaultValues: {
      productType:           'Ground up (Sale or Rent)',
      loanProgram:           'Purchase',
      state:                 'CA',
      existingPropertyType:  'Single Family Residence',
      termMonths:            12,
      interestRate:          9.5,
      originationFeePercent: 2,
      rateType:              'Fixed',
      rehabStatus:           'Not Started',
      exitStrategy:          'Sale',
      uwFee:                 999,
      costPerDraw:           500,
      avgFICO:               700,
      yearsExperience:       5,
      ...defaultValues
    }
  });

  const watchedValues = watch();

  const evaluate = useCallback(async (data: any) => {
    try {
      const payload = {
        productType:           data.productType          || '',
        loanProgram:           data.loanProgram          || '',
        state:                 data.state                || '',
        existingPropertyType:  data.existingPropertyType || '',
        loanAmount:            parseFloat(data.loanAmount)            || 0,
        purchasePrice:         parseFloat(data.purchasePrice)         || 0,
        constructionHoldback:  parseFloat(data.constructionHoldback)  || 0,
        estimatedARV:          parseFloat(data.estimatedARV)          || 0,
        estimatedAIV:          parseFloat(data.estimatedAIV)          || 0,
        termMonths:            parseInt(data.termMonths)              || 12,
        interestRate:          (parseFloat(data.interestRate) || 0) / 100,
        originationFeePercent: parseFloat(data.originationFeePercent) || 0,
        avgFICO:               parseInt(data.avgFICO)                 || 0,
        yearsExperience:       parseInt(data.yearsExperience)         || 0,
        uwFee:                 parseFloat(data.uwFee)                 || 999,
        drawFeePerDraw:        parseFloat(data.costPerDraw)           || 500,
        drawCount:             1,
        oddDaysCount:          parseInt(data.oddDaysCount)            || 0
      };
      const res = await api.post('/pricing/evaluate', payload);
      if (res.data.success) onRulesResult(res.data.data);
    } catch (_) {}
  }, [onRulesResult]);

  useEffect(() => {
    if (readOnly) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => evaluate(watchedValues), 400);
    return () => clearTimeout(debounceRef.current);
  }, [JSON.stringify(watchedValues), readOnly]); // eslint-disable-line

  const termExceedsMax = rulesResult?.termExceedsMax;
  const termBelowMin   = rulesResult?.termBelowMin;
  const minTerm = rulesResult?.minTerm ?? 6;
  const maxTerm = rulesResult?.maxTerm ?? 18;

  // Initialize app name on mount (or when loading existing loan)
  useEffect(() => {
    if (defaultValues?.applicationName) {
      setAppName(defaultValues.applicationName);
    } else {
      setAppName(buildAppName(
        watchedValues.productType,
        watchedValues.loanProgram,
        watchedValues.state,
        rulesResult?.borrowerTier
      ));
    }
  }, []); // eslint-disable-line

  // Update tier portion when rulesResult changes, preserving the rest of the name
  useEffect(() => {
    if (!readOnly && rulesResult?.borrowerTier && appName) {
      setAppName(prev => prev.replace(/T[0-9?]/, tierAbbr(rulesResult?.borrowerTier)));
    }
  }, [rulesResult?.borrowerTier]); // eslint-disable-line

  const handleFormSubmit = async (data: LoanFormData) => {
    await onSubmit({
      ...data,
      applicationName: appName,
      interestRate: (parseFloat(data.interestRate as any) || 0) / 100
    });
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">

      {/* Application Name */}
      {!readOnly && (
        <div className="bg-[#1A1D27] rounded-xl p-4 border border-gray-800">
          <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wider">
            Application Name
          </label>
          <div className="flex gap-2 items-center">
            <input
              type="text"
              value={appName}
              onChange={e => setAppName(e.target.value)}
              className={`${ic()} flex-1 font-mono`}
              placeholder="Auto-generated…"
            />
            <button
              type="button"
              title="Regenerate"
              onClick={() => setAppName(buildAppName(
                watchedValues.productType,
                watchedValues.loanProgram,
                watchedValues.state,
                rulesResult?.borrowerTier
              ))}
              className="px-3 py-2 rounded border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 text-sm transition-colors flex-shrink-0"
            >
              ↺
            </button>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Auto-generated from product · program · state · tier. You can edit or regenerate.
          </p>
        </div>
      )}

      {/* Tab navigation */}
      <div className="flex bg-[#1A1D27] rounded-lg p-1 gap-1 border border-gray-800">
        {TABS.map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-2 px-2 rounded text-xs font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-[#2563EB] text-white'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── TAB: Loan Details ── */}
      {activeTab === 'loan' && (
        <div className="bg-[#1A1D27] rounded-xl p-5 border border-gray-800">
          <div className="grid grid-cols-2 gap-4">
            <FF label="Product Type">
              <select {...register('productType')} className={sc} disabled={readOnly}>
                {PRODUCT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </FF>

            <FF label="Loan Program">
              <select {...register('loanProgram')} className={sc} disabled={readOnly}>
                <option value="Purchase">Purchase</option>
                <option value="Refinance">Refinance</option>
              </select>
            </FF>

            <FF label="Loan Amount ($)">
              <input type="number" {...register('loanAmount')} className={ic()} placeholder="425000" disabled={readOnly} />
            </FF>

            <FF label="Purchase Price ($)">
              <input type="number" {...register('purchasePrice')} className={ic()} placeholder="200000" disabled={readOnly} />
            </FF>

            <FF label="Construction Holdback ($)">
              <input type="number" {...register('constructionHoldback')} className={ic()} placeholder="350000" disabled={readOnly} />
            </FF>

            <FF label="Total Rehab Budget ($)">
              <input type="number" {...register('totalRehabBudget')} className={ic()} placeholder="350000" disabled={readOnly} />
            </FF>

            <FF label="Estimated ARV ($)">
              <input type="number" {...register('estimatedARV')} className={ic()} placeholder="600000" disabled={readOnly} />
            </FF>

            <FF label="Estimated AIV ($)">
              <input type="number" {...register('estimatedAIV')} className={ic()} placeholder="200000" disabled={readOnly} />
            </FF>

            <FF
              label={`Term (months) · range: ${minTerm}–${maxTerm} mo`}
              error={
                termExceedsMax
                  ? `Exceeds max ${maxTerm} mo — TERM_EXCEEDS_MAX`
                  : termBelowMin
                  ? `Below min ${minTerm} mo — TERM_BELOW_MIN`
                  : errors.termMonths?.message
              }
            >
              <input
                type="number"
                {...register('termMonths')}
                className={ic(termExceedsMax || termBelowMin)}
                placeholder="12"
                disabled={readOnly}
              />
            </FF>

            <FF label="Rate Type">
              <select {...register('rateType')} className={sc} disabled={readOnly}>
                <option value="Fixed">Fixed</option>
                <option value="Floating">Floating</option>
              </select>
            </FF>

            <FF
              label="Interest Rate (%)"
              hint="e.g. 9.5 for 9.5%"
              error={rulesResult?.rateBelowMin ? `Below program min ${((rulesResult.minRate || 0) * 100).toFixed(2)}%` : undefined}
            >
              <input type="number" step="0.125" {...register('interestRate')} className={ic(rulesResult?.rateBelowMin)} placeholder="9.5" disabled={readOnly} />
            </FF>

            <FF
              label="Origination Fee (%)"
              error={rulesResult?.origFeeBelowMin ? `Below program min ${((rulesResult.minOrigFee || 0) * 100).toFixed(2)}%` : undefined}
            >
              <input type="number" step="0.25" {...register('originationFeePercent')} className={ic(rulesResult?.origFeeBelowMin)} placeholder="2" disabled={readOnly} />
            </FF>

            <FF label="Exit Strategy">
              <select {...register('exitStrategy')} className={sc} disabled={readOnly}>
                <option value="Sale">Sale</option>
                <option value="Rental">Rental</option>
                <option value="Refinance">Refinance</option>
              </select>
            </FF>

            <FF label="Rehab Status">
              <select {...register('rehabStatus')} className={sc} disabled={readOnly}>
                <option value="Not Started">Not Started</option>
                <option value="In-Progress">In-Progress</option>
                <option value="Complete">Complete</option>
              </select>
            </FF>

            <FF label="Est. Funding Date">
              <input type="date" {...register('estimatedFundingDate')} className={ic()} disabled={readOnly} />
            </FF>

            <FF label="Payoff Points (%)">
              <input type="number" step="0.25" {...register('payoffPoints')} className={ic()} placeholder="0" disabled={readOnly} />
            </FF>
          </div>
        </div>
      )}

      {/* ── TAB: Property ── */}
      {activeTab === 'property' && (
        <div className="bg-[#1A1D27] rounded-xl p-5 border border-gray-800">
          <div className="grid grid-cols-2 gap-4">
            <FF label="Street Address" colSpan={2}>
              <input type="text" {...register('streetAddress')} className={ic()} placeholder="555 Fabulous Ave" disabled={readOnly} />
            </FF>

            <FF label="City">
              <input type="text" {...register('city')} className={ic()} placeholder="Kansas City" disabled={readOnly} />
            </FF>

            <FF label="State">
              <select {...register('state')} className={sc} disabled={readOnly}>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </FF>

            <FF label="ZIP">
              <input type="text" {...register('zip')} className={ic()} placeholder="64105" disabled={readOnly} />
            </FF>

            <FF label="MSA">
              <input type="text" {...register('msa')} className={ic()} placeholder="Kansas City" disabled={readOnly} />
            </FF>

            <FF label="Existing Property Type">
              <select {...register('existingPropertyType')} className={sc} disabled={readOnly}>
                {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </FF>

            <FF label="Finished Property Type">
              <select {...register('finishedPropertyType')} className={sc} disabled={readOnly}>
                {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </FF>

            <FF label="Units">
              <input type="number" {...register('units')} className={ic()} placeholder="1" disabled={readOnly} />
            </FF>

            <FF label="Est. AIV ($)">
              <input type="number" {...register('estimatedAIV')} className={ic()} placeholder="200000" disabled={readOnly} />
            </FF>

            <FF label="Est. ARV ($)">
              <input type="number" {...register('estimatedARV')} className={ic()} placeholder="600000" disabled={readOnly} />
            </FF>
          </div>
        </div>
      )}

      {/* ── TAB: Borrower ── */}
      {activeTab === 'borrower' && (
        <div className="bg-[#1A1D27] rounded-xl p-5 border border-gray-800">
          <div className="grid grid-cols-2 gap-4">
            <FF label="Sponsor Name">
              <input type="text" {...register('sponsorName')} className={ic()} placeholder="Walker Flips KC LLC" disabled={readOnly} />
            </FF>

            <FF label="Entity Name">
              <input type="text" {...register('entityName')} className={ic()} placeholder="Walker Flips KC, LLC" disabled={readOnly} />
            </FF>

            <FF label="Sponsor Approval Status">
              <select {...register('sponsorApprovalStatus')} className={sc} disabled={readOnly}>
                <option value="">Select...</option>
                <option value="Approved">Approved</option>
                <option value="Pending">Pending</option>
                <option value="Declined">Declined</option>
              </select>
            </FF>

            <FF label="Entity Approval Status">
              <select {...register('entityApprovalStatus')} className={sc} disabled={readOnly}>
                <option value="">Select...</option>
                <option value="Approved">Approved</option>
                <option value="Pending">Pending</option>
                <option value="Declined">Declined</option>
              </select>
            </FF>

            <FF label="Avg FICO Score" hint="Tier 1: ≥695 | Tier 2: ≥680 | Min: 680">
              <input type="number" {...register('avgFICO')} className={ic()} placeholder="720" disabled={readOnly} />
            </FF>

            <FF label="Years Experience" hint="Tier 1: ≥5 yrs | Tier 2: ≥2 yrs">
              <input type="number" {...register('yearsExperience')} className={ic()} placeholder="6" disabled={readOnly} />
            </FF>
          </div>
        </div>
      )}

      {/* ── TAB: Fees ── */}
      {activeTab === 'fees' && (
        <div className="bg-[#1A1D27] rounded-xl p-5 border border-gray-800">
          <div className="grid grid-cols-2 gap-4">
            <FF label="UW Fee ($)">
              <input type="number" {...register('uwFee')} className={ic()} placeholder="999" disabled={readOnly} />
            </FF>

            <FF label="Cost Per Draw ($)">
              <input type="number" {...register('costPerDraw')} className={ic()} placeholder="500" disabled={readOnly} />
            </FF>

            <FF label="Appraisal Fee ($)">
              <input type="number" {...register('appraisalFee')} className={ic()} placeholder="0" disabled={readOnly} />
            </FF>

            <FF label="Escrow & Title Fees ($)">
              <input type="number" {...register('escrowTitleFees')} className={ic()} placeholder="0" disabled={readOnly} />
            </FF>

            <FF label="Broker Fee (%)">
              <input type="number" step="0.25" {...register('brokerFeePercent')} className={ic()} placeholder="0" disabled={readOnly} />
            </FF>

            <FF label="Fee Credit ($)">
              <input type="number" {...register('feeCredit')} className={ic()} placeholder="0" disabled={readOnly} />
            </FF>

            <FF label="Payoff Liens ($)">
              <input type="number" {...register('payoffLiens')} className={ic()} placeholder="0" disabled={readOnly} />
            </FF>

            <FF label="Odd Days Count" hint="Days from close to 1st payment">
              <input type="number" {...register('oddDaysCount')} className={ic()} placeholder="0" disabled={readOnly} />
            </FF>

            <FF label="Construction Fee Type">
              <select {...register('constructionFeeType')} className={sc} disabled={readOnly}>
                <option value="">None</option>
                <option value="Charge per Draw">Charge per Draw</option>
                <option value="% of CHB">% of CHB</option>
                <option value="Flat Fee">Flat Fee</option>
              </select>
            </FF>

            <FF label="Draw Fee Payment">
              <select {...register('drawFeePaymentType')} className={sc} disabled={readOnly}>
                <option value="">None</option>
                <option value="Prepaid at Closing">Prepaid at Closing</option>
                <option value="Pay at Draw">Pay at Draw</option>
              </select>
            </FF>
          </div>
        </div>
      )}

      {/* Notes */}
      <div className="bg-[#1A1D27] rounded-xl p-4 border border-gray-800">
        <FF label="Notes">
          <textarea
            {...register('notes')}
            className={`${ic()} resize-none`}
            rows={2}
            placeholder="Internal underwriting notes..."
            disabled={readOnly}
          />
        </FF>
      </div>

      {!readOnly && (
        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] disabled:bg-gray-700 disabled:text-gray-500 text-white font-semibold py-3 rounded-lg transition-colors text-sm"
        >
          {submitting ? 'Saving...' : 'Save Loan'}
        </button>
      )}
    </form>
  );
}
